import { customElement } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from "../../../store/store";
import { StoreState } from "../../../store/store.interface";
import {
  calculateTax,
  calculateFreight,
  updateSkipFreightCalculation,
  getShippingAddress,
  getBillingAddress,
  getClientTokenResponseError,
  getTaxCalculationError,
  getTaxCalculationStatus,
  getFreightCalculationError,
  getFreightCalculationStatus,
  getBraintreeClientTokenApiStatus,
  getBraintreeHostedFieldsStatus, updateCountryCodeCurrencyCode, isShippingAddressValid, getPaymentDetails
} from '../shop-checkout';
import { BraintreeHostedFieldsStatus, ClientTokenDetails } from "../shop-checkout.interface";
import { getCart } from "../../cart/shop-cart/store/shop-cart.selectors";
import { CustomPriceRequest } from "../../cart/shop-cart/interfaces/shop-cart.interface";
import { PaymentBillingCheckoutStepBase } from "../payment-billing-checkout-step-base";
import { getPromoCode } from '../../../features/promo-code/promo-code.selectors';
import { MessageTemplateId } from '../../../common/constants/api-constants';
import { ActionStatus } from '../../../common/constants/action-status';
import { handleApiError } from '../../../common/utils/api-utils';
import { PaymentMethod } from '../../payment-methods/payment-method';
import emit from '../../../common/events';
import { getCustomPriceCart } from '../../cart/shop-cart/thunks/get-custom-price.thunk';
import { isValidApplePayDetail, isValidGooglePayDetail } from '../../../common/utils/payment-util';

/**
 * Checkout step to load payment options and capture payment details.
 */
@customElement('mhecomm-payment-billing-k12-storefront-checkout-step')
export class PaymentBillingK12StorefrontCheckoutStep extends connect(store)(PaymentBillingCheckoutStepBase) {

  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.firstUpdated(_changedProperties);
    this.focusOnSelectedOption();
  }

  stateChanged(state: StoreState) {
    this.parentStateChanged(state);
    this.canEditStep(state);

    this.handleApiFailures(state);
    this.isLoading = getBraintreeClientTokenApiStatus(state) === ActionStatus.loading
      || (this.isActive && this.availablePaymentOptions.length < 1);
    this.showNextStepApplePayPayment = !!isValidApplePayDetail(getPaymentDetails(state));
    this.showNextStepGooglePayPayment = !!isValidGooglePayDetail(getPaymentDetails(state));

    this.handleEditBillingAddress(state);
    if (getBraintreeHostedFieldsStatus(store.getState() as StoreState) && getBraintreeHostedFieldsStatus(store.getState() as StoreState) === BraintreeHostedFieldsStatus.INVALID) {
      this.braintreeHostedFieldsErrorMessage = MessageTemplateId.BRAIN_TREE_HOSTED_FIELDS_ERROR;
    } else {
      this.braintreeHostedFieldsErrorMessage = undefined;
    }
    this.updateNextStepButton(state);
  }

  /**
   * If the cart is not shippable, tax calculation will be done at the payment method step (last step).
   * Otherwise, tax calculation will be done at the shipping method step since it becomes the last step.
   */
  override async forceCompleteStep() {
    const state = store.getState() as StoreState;
    const cart = getCart(state);

    store.dispatch(
      updateCountryCodeCurrencyCode({
        currencyCode: this.currencyCode,
        countryCode: this.countryCode,
      })
    );

    if (cart && !cart.shippable && isShippingAddressValid(state)) {
      await store.dispatch(calculateTax()).unwrap();
    }

    this.isLoading = false;
  }

  override loadStep(): void {
    super.loadStep();
    this.focusOnSelectedOption();
  }

  override handleApiFailures(state: StoreState): void {
    if (getFreightCalculationStatus(state) === ActionStatus.failed) {
      handleApiError(getFreightCalculationError(state));
      return;
    }
    if (getTaxCalculationStatus(state) === ActionStatus.failed) {
      handleApiError(getTaxCalculationError(state));
      return;
    }
    if(getBraintreeClientTokenApiStatus(state) === ActionStatus.failed){
      handleApiError(getClientTokenResponseError(state));
      return;
    }
  } 

  /**
   * Check if Paypal payment should be enabled.
   */
  protected override isPaypalEnabled(_payload: ClientTokenDetails): boolean {
    return false;
  }

  /**
   * On click Review Order button, validate payment method and then calculate tax.
   */
   override async stepDoValidation() {
    if(this.selectedPaymentMethodType?.component) {
      this.selectedPaymentMethodEl = this.shadowRoot!.querySelector(this.selectedPaymentMethodType?.component) as PaymentMethod;
      try {
        await this.selectedPaymentMethodEl.validatePaymentMethod()
        await this.loadCustomPrices();
        if(this.shippable){
          await this.calculateFreight();
        } else {
          await store.dispatch(calculateTax()).unwrap();  
        }
      }
      catch(error) {
        console.error(`Failed to validate ${this.selectedPaymentMethodType.displayName}`,error);
        throw error;
      }
    }
  }

  protected async loadCustomPrices() {
    try{
      await store.dispatch(getCustomPriceCart(this.getCustomPriceBody())).unwrap();
      this.checkForCustomPriceDiscounts();
    }
    catch(error){
      console.error("Custom price failed. Use national price.");
    }
  }

  protected checkForCustomPriceDiscounts(): void {
    const cart = getCart(store.getState() as StoreState);
    if (cart && cart.products) {
      if (cart.products.some(p => p.priceInfo && p.priceInfo.customPrice && p.priceInfo.netPrice != p.priceInfo.customPrice)) {
        emit({
          element: this,
          name: 'mhecomm-custom-price',
          detail: {id: this.id},
        });
      }
    }
  }

  protected async calculateFreight() {
    store.dispatch(updateSkipFreightCalculation(false));
    await store.dispatch(calculateFreight()).unwrap();
  }

  getCustomPriceBody(): CustomPriceRequest {
    const state = store.getState() as StoreState;
    const billtoSiteId = getBillingAddress(state)?.partySiteId;
    const shiptoSiteId = getShippingAddress(state)?.partySiteId;
    const promoCode = getPromoCode(state);
    return { billtoSiteId, shiptoSiteId, promoCode };
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-payment-billing-k12-storefront-checkout-step': PaymentBillingK12StorefrontCheckoutStep;
  }
}
